/**
 * When an atomic NavVis Launch was minted as `navvis_embed` (IVION)
 * before a registered COPC existed, the block stays IVION-typed even
 * after the cloud finishes. View tour then opens a dead page:
 * "No NavVis viewer URL configured" — while `pointClouds` already
 * carries a playable COPC URL.
 *
 * Render-time rule: if there is no IVION URL but any linked set has a
 * COPC, fall through to the lidar viewer (same as `lidar_viewer`).
 * Persistent upgrade of the block type still belongs in
 * `ensureDeliverableAtomicVirtualSpace`.
 */

export function shouldFallbackNavvisEmbedToLidarViewer(input: {
  ivionUrl: string | null | undefined;
  linkedSetIds: ReadonlyArray<string | null | undefined>;
  pointClouds: Record<
    string,
    { copc_url?: string | null } | null | undefined
  >;
}): boolean {
  if (input.ivionUrl?.trim()) return false;
  for (const raw of input.linkedSetIds) {
    const id = raw?.trim();
    if (!id) continue;
    if (input.pointClouds[id]?.copc_url?.trim()) return true;
  }
  return false;
}

/** Linked set ids for a NavVis/Leica/Faro scan row (primary + join). */
export function linkedPointCloudSetIdsFromScan(scan: {
  navvis_details?: {
    point_cloud_set_id?: string | null;
    linked_point_cloud_set_ids?: string[] | null;
  } | null;
  lidar_details?: {
    point_cloud_set_id?: string | null;
    linked_point_cloud_set_ids?: string[] | null;
  } | null;
} | null | undefined): string[] {
  if (!scan) return [];
  const primary =
    scan.navvis_details?.point_cloud_set_id ??
    scan.lidar_details?.point_cloud_set_id ??
    null;
  return [
    ...new Set(
      [
        ...(scan.navvis_details?.linked_point_cloud_set_ids ?? []),
        ...(scan.lidar_details?.linked_point_cloud_set_ids ?? []),
        ...(primary ? [primary] : []),
      ].filter((id): id is string => Boolean(id?.trim())),
    ),
  ];
}
